import { useCallback, useEffect, useState } from 'react'
import { twinFetchPostJSON } from '../../../../utils/globals/data'
import { getISOCurrencyMain } from '../../../../utils/reducers/getters'
import { roundNumberByParams, preciseDecimalSum } from '@teinor/erp/prices'
import { TwinDictionary } from '../../../../utils/globals/dictionary'
import { getCurrencySymbol } from '../../../../utils/globals/price'
import TwinTrans from '../../../TwinTrans'
import { displayFormatedNumber } from '../../../../utils/globals/numbers'
import useTwinTranslation from '../../../../utils/hooks/useTwinTranslation'
import { sellTicketStatus, SellTicketStatus, sellTicketStatusInversed } from '@teinor/erp/types/company/order/sell/sellTicket'
import './sellticketstatusprogressbar.sass'

interface DSBSellTicketStatusProgressBarProps extends DSBSellTicketStatusProgressBarLogicProps {
    title?: string
}
const DSBSellTicketStatusProgressBar = ({ title, end_date, start_date }: DSBSellTicketStatusProgressBarProps) => {
    const mainCurrency = getISOCurrencyMain()
    const currencySymbol = getCurrencySymbol(mainCurrency)
    const { sellTicket } = useDSBSellTicketStatusProgressBarLogic({ end_date, start_date })
    const renderHeader: JSX.Element[] = []
    const renderThis: JSX.Element[] = []
    for (const key in sellTicket) {
        const item = sellTicket[key]
        renderHeader.push(
            <div className='sell_ticket_progress_bar_header_box' key={key}>
                <div className='flex gap-10 items-center sell_ticket_progress_bar_header_inner_legend'>
                    <div className={'sell_ticket_progress_bar_header_inner_' + key}></div>
                    <div className={'sell_ticket_progress_bar_header_inner_status_name'}>{`${item.name}  (${item.totalStatus})`}</div>
                </div>
                <div className='sell_ticket_progress_bar_header_inner_amount'>{displayFormatedNumber(item.totalImport) + ' ' + currencySymbol}</div>
            </div>
        )
        renderThis.push(<div className={'sell_ticket_progress_bar_' + key} key={key} style={{ width: item.totalStatusPer + '%' }}></div>)
    }
    if (renderHeader.length === 0) {
        renderHeader.push(<div key={'noData'}><TwinTrans transKey={'noData'} >No hay datos</TwinTrans></div>)
    }
    return (
        <div className='flex flex-col gap-20 flex-auto h-1'>
            <h3>{title}</h3>
            <div className='flex gap-x-20 overflow-y-auto'>
                {renderHeader}
            </div>
            <div className='sell_ticket_progress_bar'>
                {renderThis}
            </div>
        </div>
    )
}

interface DSBSellTicketStatusProgressBarLogicProps {
    start_date: Date
    end_date: Date
}

const useDSBSellTicketStatusProgressBarLogic = ({ end_date, start_date }: DSBSellTicketStatusProgressBarLogicProps) => {
    const [sellTicket, setSellTicket] = useState<TwinDictionary>({})
    const { t } = useTwinTranslation()
    const myStatus = JSON.stringify({
        [sellTicketStatusInversed['saved']]: t('reserved', 'Reservadas'),
        [sellTicketStatusInversed['accepted']]: t('effective', 'Efectivas'),
        [sellTicketStatusInversed['refunded']]: t('refundedsF', 'Devueltas')
    })

    const getData = useCallback(async () => {
        const myStatusParsed = JSON.parse(myStatus)
        const result = await twinFetchPostJSON('/api/app/order/tpv/sellTicket/getAllSellTicketsForStats', {
            attributes: ['total_clean_with_vat', 'total_refund_with_vat', 'status'], where: {
                'SellTicket.createdAt': { '$betweenN': [start_date, end_date] }, status: { '$notN': sellTicketStatusInversed['creating'] },
                is_last: true
            }
        })

        const statusDict: TwinDictionary = {
            saved: { totalStatus: 0, totalImport: 0, name: myStatusParsed[sellTicketStatusInversed.saved] },
            accepted: { totalStatus: 0, totalImport: 0, name: myStatusParsed[sellTicketStatusInversed.accepted] },
            refunded: { totalStatus: 0, totalImport: 0, name: myStatusParsed[sellTicketStatusInversed.refunded] }
        }
        let myTotal = 0
        if (result) {
            for (const element of result) {
                let status = sellTicketStatus[element.status as keyof SellTicketStatus]
                status = (status === 'partialCharged') ? 'saved' : status
                status = (status === 'partialRefunded' || status === 'canceled') ? 'refunded' : status
                const cleanWithVat = parseFloat( element.total_clean_with_vat || 0 )
                const refundWithVat = parseFloat( element.total_refund_with_vat || 0 )
                if ((status === 'saved' || status === 'creating') && statusDict[status]) {
                    statusDict[status].totalStatus += 1
                    statusDict[status].totalImport = parseFloat(roundNumberByParams(preciseDecimalSum(statusDict[status].totalImport, cleanWithVat)).toFixed(2))
                    myTotal++
                } else {
                    if (cleanWithVat > 0) {
                        statusDict.accepted.totalStatus += 1
                        statusDict.accepted.totalImport = parseFloat( roundNumberByParams( preciseDecimalSum( statusDict.accepted.totalImport, cleanWithVat ) ).toFixed(2) )
                    }
    
                    if (refundWithVat > 0) {
                        statusDict.refunded.totalStatus += 1
                        statusDict.refunded.totalImport = parseFloat( roundNumberByParams( preciseDecimalSum( statusDict.refunded.totalImport, refundWithVat ) ).toFixed(2) )
                    }
                    myTotal++
                }
            }

            for (const key in statusDict) {
                const item = statusDict[key]
                statusDict[key] = {
                    totalStatusPer: myTotal > 0 ? (item.totalStatus / myTotal) * 100 : 0,
                    totalImport: item.totalImport,
                    totalStatus: item.totalStatus,
                    name: item.name
                }
            }
            setSellTicket(statusDict)
        }
    }, [end_date, start_date, myStatus])

    useEffect(() => {
        getData()
    }, [getData])

    return { sellTicket }
}

export default DSBSellTicketStatusProgressBar